import { List, Root, Trigger } from '@radix-ui/react-tabs';
import { styled } from '@sds-system/system';
import { theme } from '@sds-system/theme';
import { getAllStyledProps } from '@sds-system/utils';
import { processNestedCSS } from '@sds-system/utils/styledUtils/nestedCSS';
import type {
  StyledTabListWrapperProps,
  StyledTabRootProps,
  StyledTabTriggerProps,
  TabSizeKey,
  TabVariant,
} from './Tab.types';

const tabStyle = (size: TabSizeKey, variant: TabVariant) => {
  switch (size) {
    case 'S':
      return {
        ...(variant === 'line' && theme.typography.body2.regular),
        ...(variant === 'box' && theme.typography.body2.regular),
        ...(variant === 'segment' && theme.typography.body2.medium),
      };
    case 'M':
      return {
        ...(variant === 'line' && theme.typography.body.regular),
        ...(variant === 'box' && theme.typography.body.regular),
        ...(variant === 'segment' && theme.typography.body2.medium),
      };
    case 'L':
      return {
        ...(variant === 'line' && theme.typography.header2.regular),
        ...(variant === 'box' && theme.typography.header3.regular),
        ...(variant === 'segment' && theme.typography.body.medium),
      };
    case 'XL':
      return {
        ...(variant === 'line' && theme.typography.header2.regular),
        ...(variant === 'box' && theme.typography.header3.regular),
        ...(variant === 'segment' && theme.typography.header3.medium),
      };
  }
};

const tabActiveStyle = (size: TabSizeKey, variant: TabVariant) => {
  switch (size) {
    case 'S':
      return {
        ...(variant === 'line' && theme.typography.body2.medium),
        ...(variant === 'box' && theme.typography.body2.medium),
        ...(variant === 'segment' && theme.typography.body2.medium),
      };
    case 'M':
      return {
        ...(variant === 'line' && theme.typography.body.medium),
        ...(variant === 'box' && theme.typography.body.medium),
        ...(variant === 'segment' && theme.typography.body2.medium),
      };
    case 'L':
      return {
        ...(variant === 'line' && theme.typography.header2.medium),
        ...(variant === 'box' && theme.typography.header3.medium),
        ...(variant === 'segment' && theme.typography.body.medium),
      };
    case 'XL':
      return {
        ...(variant === 'line' && theme.typography.header2.medium),
        ...(variant === 'box' && theme.typography.header3.medium),
        ...(variant === 'segment' && theme.typography.header3.medium),
      };
  }
};

export const TabRoot = styled(Root)<StyledTabRootProps>(() => {
  return {
    width: '100%',
    overflow: 'hidden',
  };
}, getAllStyledProps);

export const TabListWrapper = styled(List)<StyledTabListWrapperProps>(({ theme }) => {
  return {
    display: 'flex',
    variants: [
      {
        props: { variant: 'line' },
        style: { gap: 0 },
      },
      {
        props: { variant: 'box' },
        style: {
          gap: theme.space[12],
        },
      },
      {
        props: { variant: 'segment' },
        style: {
          borderRadius: theme.borderRadius[8],
          backgroundColor: theme.palette.bg.tertiary,
          padding: '2px',
        },
      },
      {
        props: { isGroup: true },
        style: {
          gap: theme.space[16],
          padding: '0 16px',
          width: 'max-content',
        },
      },
    ],
  };
}, getAllStyledProps);

export const TabTrigger = styled(Trigger)<StyledTabTriggerProps>(
  ({ theme, size = 'M', variant = 'line', activeStyle, inactiveStyle }) => {
    return {
      ...tabStyle(size, variant),
      height: theme.size[size],
      color: theme.palette.text.secondary,
      variants: [
        {
          props: { variant: 'line' },
          style: {
            padding: '0px 2px',
            "&[data-state='active']": processNestedCSS(
              {
                color: theme.palette.text.primary,
                height: theme.size[size] + 2,
                borderBottom: '2px solid',
                borderBottomColor: theme.palette.border.interactive.selected,
                ...tabActiveStyle(size, variant),
                ...(activeStyle && activeStyle),
              },
              theme,
            ),
            ...(inactiveStyle && inactiveStyle),
          },
        },
        {
          props: { variant: 'box' },
          style: {
            border: '1px solid',
            borderColor: theme.palette.border.secondary,
            borderRadius: theme.borderRadius[999],
            color: theme.palette.text.primary,
            "&[data-state='active']": processNestedCSS(
              {
                border: 'none',
                backgroundColor: theme.palette.bg.interactive.primary,
                color: theme.palette.text.interactive.inverse,
                ...tabActiveStyle(size, variant),
                ...(activeStyle && activeStyle),
              },
              theme,
            ),
            ...(inactiveStyle && inactiveStyle),
          },
        },
        {
          props: { variant: 'box', isGroup: true },
          style: {
            padding: '0 16px',
          },
        },
        {
          props: { variant: 'segment' },
          style: {
            "&[data-state='active']": processNestedCSS(
              {
                background: `linear-gradient(138deg, ${theme.palette.primary[500]} 2.36%, ${theme.palette.secondary[500]} 97.64%)`,
                borderRadius: theme.borderRadius[8],
                color: theme.palette.text.interactive.inverse,
                ...tabActiveStyle(size, variant),
                ...(activeStyle && activeStyle),
              },
              theme,
            ),
            ...(inactiveStyle && inactiveStyle),
          },
        },
        {
          props: { isGroup: false },
          style: {
            flex: 1,
          },
        },
      ],
    };
  },
  getAllStyledProps,
);

export const ScrollContainer = styled('div')(() => {
  return {
    overflowX: 'auto',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  };
});
